import { Button, ToggleSwitch } from 'flowbite-react';
import Pagination from 'rc-pagination';
import { SetStateAction, useEffect, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import { useMutation } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { getBriefDebriefCalls, getMyCompaniesListQuery } from 'src/apis/company';
import AMBriefDebriefContainer from 'src/components/accountManager/AMBriefDebriefContainer';
import NoAMBriefDebriefContainer from 'src/components/accountManager/NoAMBriefDebriefContainer';
import DynamicFieldSelection from 'src/components/position/DynamicFieldSelection';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';
import BriefDebriefModalV2 from 'src/components/recruiterPositions/BriefDebriefModalV2';
import Container from 'src/components/shared/Container';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import { isValidObjectId } from 'src/utils/helper';
import { BriefDebriefCallDocument } from 'types';
import { Loader } from 'ui';

const AMBriefDebriefCalls = () => {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [searchParams, setSearchParams] = useSearchParams();
	const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
	const limit = 10;
	const showRequested = searchParams.get('showRequested') === 'true';
	const filterCompany = searchParams.get('company') ?? undefined;

	const [selectedCompany, setSelectedCompany] = useState<string | undefined>(filterCompany);
	const [calls, setCalls] = useState<BriefDebriefCallDocument[]>([]);
	const [totalCalls, setTotalCalls] = useState<number>(0);
	const [companyOptions, setCompanyOptions] = useState<{ name: string; value: string }[]>([]);

	const fetchCompanyNameFn = useMutation(['getMyCompaniesListQuery'], getMyCompaniesListQuery, {
		onSuccess(data) {
			setCompanyOptions(data.companies?.map(c => ({ name: c.name, value: c._id })) ?? []);
		},
	});

	const fetchBriefDebriefCallsFn = useMutation(['getBriefDebriefCalls'], getBriefDebriefCalls, {
		onSuccess(data) {
			setCalls(data.calls as BriefDebriefCallDocument[]);
			setTotalCalls(data.total ?? 0);
		},
		onError(error: Error) {},
	});

	useEffect(() => {
		fetchBriefDebriefCallsFn.mutate({ companyId: selectedCompany, page, limit, showRequested });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedCompany, page, limit, showRequested]);

	const hasCalls = calls.length > 0;

	return (
		<>
			<Container>
				<div className="flex justify-between items-center py-2 pt-6 sm:pt-[10px] border-b border-[#EBEBF0] dark:border-[#555770] border-solid">
					<div className="text-lg font-semibold dark:text-white">Brief - Debrief</div>
					<Button color="purplePlaced" onClick={onOpen}>
						<MdAdd className="mr-2" /> New Brief/Debrief
					</Button>
				</div>
				<DynamicFieldSelection
					dynamicClassName={'md:max-w-[25%] mt-5'}
					onChange={e => {
						if (isValidObjectId(e)) {
							setSelectedCompany(e);
							setPage(1);
							searchParams.set('company', e);
							searchParams.set('page', '1');
							setSearchParams(searchParams);
						} else if (!e) {
							setSelectedCompany(undefined);
							setPage(1);
							searchParams.set('company', e);
							searchParams.set('page', '1');
							setSearchParams(searchParams);
						}
					}}
					onType={e => fetchCompanyNameFn.mutate(e)}
					options={companyOptions}
					placeholder="Select a Company (Type to search)"
					creatable={true}
				/>
				<ToggleSwitch
					className="mt-3"
					label="Show Requested Calls"
					checked={showRequested}
					onChange={e => {
						searchParams.set('showRequested', e.toString());
						searchParams.set('page', '1');
						setPage(1);
						setSearchParams(searchParams);
					}}
				/>
				{fetchBriefDebriefCallsFn.isLoading ? (
					// <Loader />
					<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto " />
				) : hasCalls ? (
					<div className="pt-1 pb-5">
						<Pagination
							total={totalCalls}
							className="flex justify-center"
							pageSize={limit}
							current={Number(page)}
							showTotal={total =>
								`Showing ${(Number(page) - 1) * Number(limit) + 1}-${Number(page) * Number(limit)} of ${total} contests`
							}
							showSizeChanger={true}
							onChange={(p: SetStateAction<any>) => {
								setPage(p);
								searchParams.set('page', p);
								setSearchParams(searchParams);
							}}
						/>
						<AMBriefDebriefContainer calls={calls} />
						<Pagination
							total={totalCalls}
							className="flex justify-center"
							pageSize={limit}
							current={Number(page)}
							showTotal={total =>
								`Showing ${(Number(page) - 1) * Number(limit) + 1}-${Number(page) * Number(limit)} of ${total} contests`
							}
							showSizeChanger={true}
							onChange={(p: SetStateAction<any>) => {
								setPage(p);
								searchParams.set('page', p);
								setSearchParams(searchParams);
							}}
						/>
					</div>
				) : (
					<>
						<div className="h-[calc(100vh-70px)] sm:h-[calc(100vh-140px)] min-h-[350px] flex flex-col items-center justify-center">
							<NoAMBriefDebriefContainer isCompanySelected={!!selectedCompany} />
						</div>
					</>
				)}
			</Container>

			<BriefDebriefModalV2 isOpen={isOpen} onClose={onClose} />
		</>
	);
};

export default AMBriefDebriefCalls;
